const MOBILE_DESKTOP_RECOMMENDATION_SEEN_ON_KEY = 'laudopsi:mobile-desktop-recommendation-seen-on';

/**
 * Mirrors the stored day so a single page life never shows the overlay twice.
 * It dies with the JavaScript realm, so it is not what protects a reload.
 */
let seenOnInMemory: string | null = null;

/**
 * The local calendar day, not the UTC one: "uma vez por dia" has to mean the
 * day the psychologist is living, and Brazil is always behind UTC.
 */
const getToday = () => {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');

    return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Reading the property can throw on its own in restricted contexts, so every
 * access has to sit inside the `try`.
 */
const STORAGE_READERS: Array<() => Storage> = [
    () => window.localStorage,
    () => window.sessionStorage,
];

/**
 * Every storage is read, not just the one that ends up holding the day: a
 * `localStorage` that hit its quota still *keeps* the day it recorded earlier,
 * and ignoring it would show the overlay again in each new tab.
 */
const isDayStoredAnywhere = (day: string) => {
    for (const readStorage of STORAGE_READERS) {
        try {
            if (readStorage().getItem(MOBILE_DESKTOP_RECOMMENDATION_SEEN_ON_KEY) === day) {
                return true;
            }
        } catch {
            // Unreadable storage simply has nothing to tell us.
        }
    }

    return false;
};

export const hasSeenMobileDesktopRecommendationToday = () => {
    const today = getToday();

    if (seenOnInMemory === today) return true;
    // There is no SSR here, so a missing `window` means a runtime where showing
    // a mobile overlay makes no sense at all.
    if (typeof window === 'undefined') return true;

    return isDayStoredAnywhere(today);
};

/**
 * Returns whether the day made it to a storage that survives a reload.
 * `localStorage` keeps it across tabs; `sessionStorage` still keeps it across
 * reloads of this tab, which is all that private or embedded contexts leave us.
 *
 * The write attempted here is the real marker, never a smaller probe: a storage
 * that accepts a probe can still reject the value we actually need to store.
 */
export const markMobileDesktopRecommendationSeenToday = (): boolean => {
    const today = getToday();
    seenOnInMemory = today;

    if (typeof window === 'undefined') return false;

    for (const readStorage of STORAGE_READERS) {
        try {
            readStorage().setItem(MOBILE_DESKTOP_RECOMMENDATION_SEEN_ON_KEY, today);

            return true;
        } catch {
            // Storage can be unavailable, or full, in private or restricted
            // browser contexts — try the next one.
        }
    }

    return false;
};

/**
 * Signing in is a strong enough signal to override the once-a-day budget: the
 * person deliberately came back to the platform, and this is the moment the
 * desktop nudge is most likely to land.
 *
 * Clears every storage, not just the one that accepted the write: a day left
 * behind in the other one would defeat the re-arm.
 */
export const clearMobileDesktopRecommendationSeen = () => {
    seenOnInMemory = null;

    if (typeof window === 'undefined') return;

    for (const readStorage of STORAGE_READERS) {
        try {
            readStorage().removeItem(MOBILE_DESKTOP_RECOMMENDATION_SEEN_ON_KEY);
        } catch {
            // Storage can be unavailable in private or restricted browser contexts.
        }
    }
};
